import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const DUAL_RE =
  /^\s*THOUGHT:\s*[\s\S]*?\s*SPEAK:\s*([\s\S]*?)(?:\s*NOTES:\s*[\s\S]*)?$/i;
const SPEAK_ONLY_RE = /^\s*SPEAK:\s*([\s\S]*?)(?:\s*NOTES:\s*[\s\S]*)?$/i;
const NOTES_TAIL_RE = /\s*NOTES:\s*\{[\s\S]*\}\s*$/i;

export function extractSpeak(raw: string): string {
  const text = (raw || "").trim();
  if (!text) return "";
  const dual = text.match(DUAL_RE);
  if (dual?.[1]) return dual[1].trim();
  const speak = text.match(SPEAK_ONLY_RE);
  if (speak?.[1]) return speak[1].trim();
  return text.replace(NOTES_TAIL_RE, "").trim();
}

function renderInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={idx} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code
          key={idx}
          className="rounded bg-background/80 px-1 py-0.5 font-mono text-[0.85em]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

function isBullet(line: string): boolean {
  return /^\s*[-*]\s+\S/.test(line);
}

function isNumbered(line: string): boolean {
  return /^\s*\d+\.\s+\S/.test(line);
}

function bulletText(line: string): string {
  return line.replace(/^\s*[-*]\s+/, "").replace(/^\s*\d+\.\s+/, "");
}

function headingText(line: string): string | null {
  const hash = line.trim().match(/^#{1,3}\s+(.+)$/);
  if (hash?.[1]) return hash[1].trim();
  const bold = line.trim().match(/^\*\*([^*]+)\*\*$/);
  if (bold?.[1]) return bold[1].trim();
  return null;
}

function AnswerBody({
  text,
  className,
  stripScaffold = false,
}: {
  text: string;
  className?: string;
  stripScaffold?: boolean;
}) {
  const body = stripScaffold ? extractSpeak(text) : (text || "").trim();
  if (!body) return null;
  const lines = body.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const head = headingText(line);
    if (head) {
      blocks.push(
        <h3
          key={`h-${blocks.length}`}
          className="pt-2 text-sm font-semibold tracking-tight text-slate-900 first:pt-0"
        >
          {head}
        </h3>,
      );
      i += 1;
      continue;
    }
    if (isBullet(line) || isNumbered(line)) {
      const numbered = isNumbered(line);
      const items: string[] = [];
      while (i < lines.length && (numbered ? isNumbered(lines[i]) : isBullet(lines[i]))) {
        items.push(bulletText(lines[i]));
        i += 1;
      }
      const List = numbered ? "ol" : "ul";
      blocks.push(
        <List
          key={`list-${blocks.length}`}
          className={cn(
            "space-y-1.5 pl-5",
            numbered ? "list-decimal" : "list-disc",
          )}
        >
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const chunk: string[] = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !headingText(lines[i]) &&
      !isBullet(lines[i]) &&
      !isNumbered(lines[i])
    ) {
      chunk.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={`p-${blocks.length}`} className="whitespace-pre-wrap leading-relaxed">
        {renderInline(chunk.join("\n"))}
      </p>,
    );
  }
  return <div className={cn("space-y-3", className)}>{blocks}</div>;
}

export default AnswerBody;
